/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Login from '../pages/Login';
import Register from '../pages/Register';
import Assessment from '../pages/Assessment';
import DobPromptModal from './auth/DobPromptModal';
import Dashboard from '../pages/Dashboard';
import Workspace from './workspace/Workspace';
import SkillBotPage from '../pages/SkillBotPage';
import CommunityPage from '../pages/CommunityPage';
import ResumeUpload from '../pages/ResumeUpload';
import RoadmapView from './Roadmap/RoadmapView';
import CareerGraph from '../pages/CareerGraph';
import LearningHub from '../pages/LearningHub';
import KnowledgeBase from '../pages/KnowledgeBase';
import ProfileSettings from './ProfileSettings';
import Sidebar from './Sidebar';
import Footer from './Footer'; // Only for login/register pages

// Conference Sprint S2a: Cricket Dashboard for Civil Construction
import CricketDashboardPage from '../pages/CricketDashboardPage';
import CricketDashboardLanding from '../pages/CricketDashboardLanding';

// SkillCode — AI Software Engineering Intelligence
import SkillCode from '../pages/SkillCode';
import SkillCodePage from '../pages/SkillCodePage';

// Civil-vertical route guard for RCR-Layout (declared above the lazy const so
// `import/first` is satisfied — all imports stay above any module-level code).
import CivilRouteGuard from './CivilRouteGuard';

// ShubbhYatra Phase 1: web blocker shown when features.shubbhYatraOnly===true.
import ShubbhYatraOnlyBlocker from './ShubbhYatraOnlyBlocker';

// Putaway Intelligence — WMS-domain research case study with Plotly charts.
// React.lazy keeps the page (and its ~700KB plotly.js-dist-min bundle) out
// of main.js so non-WMS users never download it.
const PutawayIntelligencePage = React.lazy(() => import('../pages/PutawayIntelligencePage'));

// RCR-Layout — Civil-vertical layout planning surface.
// Lazy at AuthWrapper so non-Civil users never download the editor chunk.
const RcrLayoutPage = React.lazy(() => import('../pages/RcrLayoutPage'));

const AuthWrapper = () => {
  const { user } = useAuth();

  // DPDP age gating: prompt for DOB when the account has no verified age.
  // Soft gate — dismissible today; a future AGE_HARD_GATE_ENFORCED_AT flag
  // will surface via the login response and make this modal non-dismissible.
  const [dobModalOpen, setDobModalOpen] = useState(false);
  useEffect(() => {
    if (!user) return;
    const needsDob = user.requiresDob === true || user.ageStatus === 'unknown';
    setDobModalOpen(needsDob);
  }, [user]);

  // Unauthenticated routes
  if (!user) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/cricket-dashboard" element={<CricketDashboardLanding />} />
            <Route path="/skillcode/*" element={<SkillCodePage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
        <Footer hasSidebar={false} />
      </div>
    );
  }

  // ShubbhYatra: field users (allowSkillDelivery=false) see ONE blocker
  // page UNLESS their Travel modules include a web-friendly module
  // (skillbot / learning / roadmap). Travel-only reps whose modules are
  // mobile-only (just 'connect') still get the "please use the app"
  // blocker — nothing changed for them. Travel partners/learners whose
  // modules include a web surface land in a module-filtered sidebar
  // (see Sidebar.js). Unflagged users: this branch is never taken.
  if (user.features && user.features.shubbhYatraOnly === true) {
    const modules = Array.isArray(user?.shubbhYatraConfig?.modules)
      ? user.shubbhYatraConfig.modules
      : [];
    const WEB_MODULES = new Set(['skillbot', 'learning', 'roadmap']);
    const hasWebModule = modules.some((m) => WEB_MODULES.has(m));
    if (!hasWebModule) {
      return <ShubbhYatraOnlyBlocker />;
    }
    // else: fall through to the normal authed layout. Sidebar.js
    // filters navItems down to the resolved modules; non-module
    // routes rendered below are hidden from the nav but still
    // reachable by URL — the server's requireSkillDeliveryAllowed
    // middleware is the authoritative gate on data.
  }

  // Authenticated routes with sidebar and footer at bottom of main content
  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <main className="flex-1" style={{ marginBottom: '10px' }}>
          <Routes>
            {/* Main Dashboard */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Workspace Shell - Week 1-2 */}
            <Route path="/workspace" element={<Workspace />} />

            {/* SkillBot - Dedicated full-page chat */}
            <Route path="/skillbot" element={<SkillBotPage />} />

            {/* Profile Settings */}
            <Route path="/profile" element={<ProfileSettings />} />

            {/* Profile Upload & Roadmap */}
            <Route path="/upload" element={<ResumeUpload />} />
            <Route path="/upload-resume" element={<ResumeUpload />} />
            <Route path="/upload-profile" element={<ResumeUpload />} />
            <Route path="/roadmap" element={<RoadmapView />} />

            {/* Career Graph - Neo4j Knowledge Graph Visualization */}
            <Route path="/career-graph" element={<CareerGraph />} />

            {/* Learning Hub - Curated Resources */}
            <Route path="/learning" element={<LearningHub />} />
            <Route path="/learning-hub" element={<LearningHub />} />

            {/* P4 skill assessment — profile, skill check, roadmap */}
            <Route path="/assessment" element={<Assessment />} />

            {/* Knowledge Base - Document Management & RAG */}
            <Route path="/knowledge-base" element={<KnowledgeBase />} />

            {/* SkillCode - AI Software Engineering Intelligence */}
            {/* Trailing /* lets SkillCode's nested <Routes> render its sub-pages. */}
            <Route path="/skillcode/*" element={<SkillCode />} />

            {/* Community - Unified networking hub (includes Network Intelligence) */}
            <Route path="/community" element={<CommunityPage />} />

            {/* Putaway Intelligence - WMS research case study (React.lazy'd) */}
            <Route
              path="/wms/putaway-intelligence"
              element={
                <React.Suspense fallback={<div className="p-10 text-gray-500">Loading Putaway Intelligence…</div>}>
                  <PutawayIntelligencePage />
                </React.Suspense>
              }
            />

            {/* Cricket Dashboard - EVM for Civil Construction (Conference Sprint S2a) */}
            <Route path="/cricket-dashboard" element={<CricketDashboardPage />} />

            {/* RCR-Layout - Civil-vertical layout planner (PR1 scaffold).
                Civil-only via CivilRouteGuard (stricter than cricket-dashboard
                because the editor writes per-user data). React.lazy'd. */}
            <Route
              path="/rcr-layout"
              element={
                <CivilRouteGuard>
                  <React.Suspense fallback={<div className="p-10 text-gray-500">Loading RCR-Layout…</div>}>
                    <RcrLayoutPage />
                  </React.Suspense>
                </CivilRouteGuard>
              }
            />

            {/* Backward compatibility redirects */}
            <Route path="/xconnect" element={<Navigate to="/community" replace />} />
            <Route path="/network" element={<Navigate to="/community?tab=network" replace />} />
            <Route path="/network-intelligence" element={<Navigate to="/community?tab=network" replace />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer hasSidebar={true} />
      </div>
      <DobPromptModal
        open={dobModalOpen}
        dismissible={true}
        onClose={() => setDobModalOpen(false)}
        onSaved={() => setDobModalOpen(false)}
      />
    </div>
  );
};

export default AuthWrapper;
